import React, { useState, useMemo, useCallback } from "react";
import { Modal, Row, Col, Input, Select, Card, Button, Space, InputNumber, Alert, Spin, Tag, Typography } from "antd";
import { SearchOutlined, ShoppingCartOutlined, DeleteOutlined } from "@ant-design/icons";
import { useCustom } from "@refinedev/core";
import { SOURCING_CANDIDATES_QUERY } from "../queries";
import { BidItem, CandidateItem, CartEntry } from "../interface";

const { Text } = Typography;

interface ItemSelectionDialogProps {
  open: boolean;
  onCancel: () => void;
  onConfirm: (bidItems: BidItem[]) => void;
}

const buildAllocationsFromCart = (cartEntries: CartEntry[]) => {
  return cartEntries.map(entry => ({
    prId: entry.candidate.prId,
    prItemId: entry.candidate.itemId,
    quantity: entry.selectedQty,
  }));
};

const consolidateCartToBidItems = (cartEntries: CartEntry[]): BidItem[] => {
  const grouped: Record<string, { item: BidItem; allocations: ReturnType<typeof buildAllocationsFromCart> }> = {};
  let counter = 0;

  for (const entry of cartEntries) {
    const name = entry.candidate.itemName.toLowerCase().trim();
    if (!grouped[name]) {
      grouped[name] = {
        item: {
          id: `consolidated-${counter++}`,
          name: entry.candidate.itemName,
          description: entry.candidate.description,
          quantity: 0,
          unit: entry.candidate.unit,
          unitPrice: entry.candidate.unitPrice,
          totalPrice: 0,
          specifications: undefined,
          tags: [],
          allocations: [],
        },
        allocations: [],
      };
    }

    const alloc = buildAllocationsFromCart([entry]);
    grouped[name].item.quantity += entry.selectedQty;
    grouped[name].item.totalPrice = grouped[name].item.quantity * (entry.candidate.unitPrice || 0);
    (grouped[name].item.allocations as any[]).push(...(alloc as any));
  }

  return Object.values(grouped).map(g => g.item);
};

export const ItemSelectionDialog: React.FC<ItemSelectionDialogProps> = ({ open, onCancel, onConfirm }) => {
  const [search, setSearch] = useState("");
  const [categoryType, setCategoryType] = useState<string | undefined>(undefined);
  const [department, setDepartment] = useState("");
  const [prNumber, setPrNumber] = useState("");
  const [productCategory, setProductCategory] = useState("");
  const [cart, setCart] = useState<CartEntry[]>([]);
  const [qtyInputs, setQtyInputs] = useState<Record<string, number>>({});

  const { data: candidatesData, isLoading: candidatesLoading } = useCustom<any>({
    url: '',
    method: 'get',
    meta: {
      gqlQuery: SOURCING_CANDIDATES_QUERY,
      gqlVariables: {
        search: search || undefined,
        categoryType: categoryType || undefined,
        productCategory: productCategory || undefined,
        department: department || undefined,
        prNumber: prNumber || undefined,
      },
    },
  });

  const candidates: CandidateItem[] = useMemo(() => {
    const raw = (candidatesData?.data as any)?.sourcingCandidates;
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    return [];
  }, [candidatesData]);

  const addToCart = useCallback((candidate: CandidateItem) => {
    const key = `${candidate.prId}-${candidate.itemId}`;
    const qty = qtyInputs[key] || 1;
    setCart(prev => {
      const existing = prev.findIndex(e => e.key === key);
      if (existing >= 0) {
        const updated = [...prev];
        const newQty = updated[existing].selectedQty + qty;
        if (newQty > candidate.remainingQty) {
          updated[existing].selectedQty = candidate.remainingQty;
        } else {
          updated[existing].selectedQty = newQty;
        }
        return updated;
      }
      return [...prev, { key, candidate, selectedQty: Math.min(qty, candidate.remainingQty) }];
    });
  }, [qtyInputs]);

  const removeFromCart = useCallback((key: string) => {
    setCart(prev => prev.filter(e => e.key !== key));
  }, []);

  const updateCartQty = useCallback((key: string, qty: number | null) => {
    setCart(prev => prev.map(e => e.key === key ? { ...e, selectedQty: qty || 0 } : e));
  }, []);

  const clearCart = useCallback(() => {
    setCart([]);
  }, []);

  const handleConfirm = useCallback(() => {
    const bidItems = consolidateCartToBidItems(cart);
    onConfirm(bidItems);
    setCart([]);
    setQtyInputs({});
  }, [cart, onConfirm]);

  const handleCancel = useCallback(() => {
    setCart([]);
    setQtyInputs({});
    onCancel();
  }, [onCancel]);

  const cartTotalQty = cart.reduce((sum, e) => sum + e.selectedQty, 0);

  const showInitialPrompt = !search && !categoryType && !department && !prNumber && !productCategory && candidates.length === 0 && !candidatesLoading;

  return (
    <Modal
      title="Select Items for Sourcing"
      open={open}
      onCancel={handleCancel}
      width="90vw"
      style={{ maxWidth: 1400, top: 20 }}
      footer={null}
      destroyOnClose
    >
      <Row gutter={16}>
        <Col xl={16} xs={24}>
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search by item name or description..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
            style={{ marginBottom: 12 }}
          />

          <Row gutter={[8, 8]} style={{ marginBottom: 12 }}>
            <Col xs={24} sm={12} lg={6}>
              <Select
                placeholder="Category Type"
                value={categoryType}
                onChange={setCategoryType}
                allowClear
                style={{ width: '100%' }}
              >
                <Select.Option value="GOODS">Goods</Select.Option>
                <Select.Option value="SERVICES">Services</Select.Option>
                <Select.Option value="WORKS">Works</Select.Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Input
                placeholder="Department"
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Input
                placeholder="PR Number"
                value={prNumber}
                onChange={(e) => setPrNumber(e.target.value)}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Input
                placeholder="Product Category"
                value={productCategory}
                onChange={(e) => setProductCategory(e.target.value)}
                allowClear
              />
            </Col>
          </Row>

          {candidatesLoading ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <Spin size="large" />
            </div>
          ) : showInitialPrompt ? (
            <Alert
              type="info"
              message="Use the search and filters above to find approved PR items that can be sourced."
              style={{ marginBottom: 12 }}
              showIcon
            />
          ) : candidates.length === 0 ? (
            <Alert
              type="warning"
              message="No items found matching your criteria."
              style={{ marginBottom: 12 }}
              showIcon
            />
          ) : (
            <div style={{ maxHeight: 'calc(100vh - 320px)', overflowY: 'auto', paddingRight: 4 }}>
              <Row gutter={[12, 12]}>
                {candidates.map((candidate) => {
                  const key = `${candidate.prId}-${candidate.itemId}`;
                  const inCart = cart.find(e => e.key === key);
                  const cartQty = inCart?.selectedQty || 0;
                  const remaining = candidate.remainingQty - cartQty;

                  return (
                    <Col xs={24} sm={12} lg={8} key={key}>
                      <Card size="small">
                        <div style={{ marginBottom: 4 }}>
                          <Text strong style={{ fontSize: 14 }}>{candidate.itemName}</Text>
                          <Tag style={{ marginLeft: 6 }}>{candidate.categoryType}</Tag>
                        </div>
                        <div style={{
                          color: '#666',
                          fontSize: 12,
                          marginBottom: 6,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical' as any,
                        }}>
                          {candidate.description}
                        </div>
                        <div style={{ fontSize: 12, color: '#888', marginBottom: 2 }}>
                          PR: {candidate.prNumber}
                        </div>
                        <div style={{ fontSize: 12, color: '#888', marginBottom: 2 }}>
                          Remaining: {candidate.remainingQty} {candidate.unit}
                        </div>
                        <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>
                          Unit Price: ETB {candidate.unitPrice?.toLocaleString()}
                        </div>
                        {inCart && (
                          <Tag color="blue" style={{ marginBottom: 6 }}>
                            {cartQty} in cart
                          </Tag>
                        )}
                        <Space size={4}>
                          <InputNumber
                            min={1}
                            max={Math.max(1, remaining)}
                            value={qtyInputs[key] || 1}
                            onChange={(val) => setQtyInputs(prev => ({ ...prev, [key]: val || 1 }))}
                            size="small"
                            style={{ width: 70 }}
                          />
                          <Button
                            size="small"
                            type="primary"
                            disabled={remaining <= 0}
                            onClick={() => addToCart(candidate)}
                          >
                            Add
                          </Button>
                        </Space>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            </div>
          )}
        </Col>

        <Col xl={8} xs={24}>
          <div style={{ position: 'sticky', top: 0 }}>
            <Card
              title="Cart"
              extra={
                cart.length > 0 && (
                  <Button size="small" onClick={clearCart}>
                    Clear Cart
                  </Button>
                )
              }
            >
              <div style={{ marginBottom: 12 }}>
                <Text strong>
                  <ShoppingCartOutlined /> Cart ({cart.length} items, {cartTotalQty} total qty)
                </Text>
              </div>

              {cart.length === 0 ? (
                <Text type="secondary">No items selected</Text>
              ) : (
                <div style={{ maxHeight: 'calc(100vh - 400px)', overflowY: 'auto' }}>
                  {cart.map((entry) => (
                    <div
                      key={entry.key}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '6px 0',
                        borderBottom: '1px solid #f0f0f0',
                        gap: 8,
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, color: '#888' }}>
                          {entry.candidate.prNumber}
                        </div>
                        <div style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {entry.candidate.itemName}
                        </div>
                      </div>
                      <InputNumber
                        min={1}
                        max={entry.candidate.remainingQty}
                        value={entry.selectedQty}
                        onChange={(val) => updateCartQty(entry.key, val)}
                        size="small"
                        style={{ width: 65 }}
                      />
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        size="small"
                        onClick={() => removeFromCart(entry.key)}
                      />
                    </div>
                  ))}
                </div>
              )}

              <div style={{ marginTop: 12, textAlign: 'right' }}>
                <Button
                  type="primary"
                  disabled={cart.length === 0}
                  onClick={handleConfirm}
                >
                  Confirm Selection
                </Button>
              </div>
            </Card>
          </div>
        </Col>
      </Row>
    </Modal>
  );
};
